import React, { useState, useEffect } from 'react';
import { buyerTypes } from '../utils/constants';
import Select from './Select';
import Radio from './Radio';
import InputField from './InputField';
import Button from './Button';
import useReCaptcha, { RecaptchaComponent } from '../utils/recaptcha';
import api from '../services/api';

export const FooterForm = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        postCode: '',
        buyerType: 'Buyer Type',
        howDidYouHearAboutUs: 'How did you hear about us?'
    });
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const { recaptchaRef, executeRecaptcha } = useReCaptcha();

    const [enquireSource, setEnquireSource] = useState([
        "Billboards",
        "Builder Referral",
        "Digital H&L Collections Book",
        "Display Village",
        "Facebook",
        "Family/Friends",
        "Google",
        "Instagram",
        "Local Knowledge",
        "Local Paper",
        "Newsletter",
        "Newspaper Metro",
        "Openlot.com.au",
        "Other",
        "Radio",
        "Signage",
        "Website",
        "realestate.com.au",
    ]);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const project = await api.getProject();
                if (project.frontendEnquirySources && project.frontendEnquirySources.length) {
                    setEnquireSource(project.frontendEnquirySources.map((x) => x.name));
                }
            } catch (err) {
                // ignore error; fallback to default enquireSource
            }
        };
        fetchProject();
    }, []);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Postcode validation (Australian format)
        if (formData.postCode) {
            const postcodeRegex = /^[0-9]{4}$/;
            if (!postcodeRegex.test(formData.postCode)) {
                newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
            }
        } else {
            newErrors.postCode = 'Please enter a valid 4-digit Australian postcode';
        }

        // Buyer Type validation
        if (formData.buyerType === 'Buyer Type') {
            newErrors.buyerType = 'Please select a buyer type';
        }

        // How did you hear about us validation
        if (formData.howDidYouHearAboutUs === 'How did you hear about us?') {
            newErrors.howDidYouHearAboutUs = 'Please select how you heard about us';
        }

        // Phone validation (basic)
        const phoneRegex = /^[0-9+\s()-]{8,15}$/;
        if (!phoneRegex.test(formData.mobilePhone)) {
            newErrors.mobilePhone = 'Please enter a valid phone number';
        }

        // Terms validation
        if (!isChecked) {
            newErrors.terms = 'You must agree to the privacy policy';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e && e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            try {
                // Get reCAPTCHA token using the new method with action parameter
                const token = await executeRecaptcha('footer_form');

                // Create form data for submission
                const formDataToSubmit = new URLSearchParams();
                formDataToSubmit.append('firstName', formData.firstName);
                formDataToSubmit.append('lastName', formData.lastName);
                formDataToSubmit.append('email', formData.email);
                formDataToSubmit.append('postCode', formData.postCode);
                formDataToSubmit.append('buyerType', formData.buyerType);
                formDataToSubmit.append('mobilePhone', formData.mobilePhone);
                formDataToSubmit.append('howDidYouHearAboutUs', formData.howDidYouHearAboutUs);

                // Add reCAPTCHA token
                formDataToSubmit.append('g-recaptcha-response', token);

                // API endpoint from environment variable
                const apiUrl = `${process.env.REACT_APP_API_URL}/enquire`;

                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: formDataToSubmit,
                });

                if (response.ok) {
                    // Success handling
                    setIsSubmitted(true);
                    setIsSubmitting(false);

                    // REA conversion for footer form success (no redirect)
                    try {
                        window.REA.track_conversion?.("23861");
                        console.log('Conversion checked!');
                    } catch (error) {
                        console.error('Conversion error:', error);
                    }

                    // Reset form
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        postCode: '',
                        mobilePhone: '',
                        buyerType: 'Buyer Type',
                        howDidYouHearAboutUs: 'How did you hear about us?'
                    });
                    setIsChecked(false);
                } else {
                    const errorData = await response.json();
                    console.error('Form submission error:', errorData);
                    setIsSubmitting(false);
                    setErrors(prev => ({
                        ...prev,
                        general: errorData.message || 'Something went wrong. Please try again.'
                    }));
                }
            } catch (error) {
                console.error('Form submission error:', error);
                setIsSubmitting(false);
                setErrors(prev => ({
                    ...prev,
                    general: 'reCAPTCHA verification failed. Please try again.'
                }));
            }
        }
    };

    return (
        <div>
            {isSubmitted ? (
                <div className="bg-gableGreen bg-opacity-30 text-white p-6 rounded-lg mb-10 w-full text-center shadow-lg">
                    <h3 className="text-xl font-semibold mb-3">Thank you for your message!</h3>
                    <p className="text-base">We'll be in touch with you shortly.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    <RecaptchaComponent ref={recaptchaRef} />

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        <div className="w-full">
                            <InputField
                                placeholder="First Name"
                                variant="polar"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                            />
                            {errors.firstName && <span className="text-red-500 text-xs mt-1">{errors.firstName}</span>}
                        </div>
                        <div className="w-full">
                            <InputField
                                placeholder="Last Name"
                                variant="polar"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                            />
                            {errors.lastName && <span className="text-red-500 text-xs mt-1">{errors.lastName}</span>}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        <div className="w-full">
                            <InputField
                                placeholder="Email"
                                variant="polar"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                            />
                            {errors.email && <span className="text-red-500 text-xs mt-1">{errors.email}</span>}
                        </div>
                        <div className="w-full">
                            <InputField
                                placeholder="Mobile Phone"
                                variant="polar"
                                value={formData.mobilePhone}
                                onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                            />
                            {errors.mobilePhone && <span className="text-red-500 text-xs mt-1">{errors.mobilePhone}</span>}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        <div className="w-full">
                            <InputField
                                placeholder="Postcode (4 digits)"
                                variant="polar"
                                value={formData.postCode}
                                onChange={(e) => handleInputChange('postCode', e.target.value)}
                                maxLength="4"
                            />
                            {errors.postCode && <span className="text-red-500 text-xs mt-1">{errors.postCode}</span>}
                        </div>
                        <div className="w-full z-20">
                            <Select
                                defaultValue={formData.buyerType}
                                options={[...buyerTypes]}
                                onChange={(value) => handleInputChange('buyerType', value)}
                                color="polar"
                                menuBgClass="bg-gableGreen"
                            />
                            {errors.buyerType && <span className="text-red-500 text-xs mt-1">{errors.buyerType}</span>}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        <div className="w-full z-10">
                            <Select
                                defaultValue={formData.howDidYouHearAboutUs}
                                options={enquireSource}
                                onChange={(value) => handleInputChange('howDidYouHearAboutUs', value)}
                                color="polar"
                                menuBgClass="bg-gableGreen"
                            />
                            {errors.howDidYouHearAboutUs && <span className="text-red-500 text-xs mt-1">{errors.howDidYouHearAboutUs}</span>}
                        </div>
                    </div>

                    <div className="flex gap-4 items-center mt-2">
                        <div onClick={() => {
                            setIsChecked(!isChecked);
                            if (errors.terms) {
                                setErrors(prev => ({ ...prev, terms: '' }));
                            }
                        }}>
                            <Radio checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
                        </div>
                        <label className="text-xs sm:text-sm font-mori text-white">
                            I agree to the <a href="/our-policies" target="_blank" rel="noopener noreferrer" className="underline text-sycamore hover:text-white transition-colors">Privacy Policy</a>
                        </label>
                    </div>
                    {errors.terms && <span className="text-red-500 text-xs mt-1">{errors.terms}</span>}

                    {errors.general && <div className="text-red-500 text-sm mt-2">{errors.general}</div>}

                    <div className="mt-6">
                        <Button
                            title={isSubmitting ? "Submitting..." : "Submit"}
                            className="w-full sm:w-auto"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        />
                    </div>
                </form>
            )}
        </div>
    );
};

export default FooterForm;